import React, { FC, useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import DataTable from 'components/shared/DataTable';
import { Column } from 'components/shared/DataTable/DataTable';
import Grid from 'components/UI/Grid';
import Loading from 'components/UI/Loading/Loading';
import { Paper } from '@mui/material';
import { FilterMatchMode } from 'primereact/api';
import { DataTableRowClickEvent } from 'primereact/datatable';
import { Agent } from 'models/restTypes';

const StyledGrid = styled(Grid)`
  display: flex;
  flex: unset;
  height: 100%;
  width: 100%;
  align-items: normal;
`;

const DataTableContainer = styled(Paper)`
  overflow: hidden;
  height: 100%;
  background-color: var(--surface-ground);
`;

const AgentsTable: FC<{
  agents: Agent[];
  isLoading: boolean;
}> = observer(({ agents, isLoading }) => {
  const { t } = useTranslation('staticData');

  const [filters] = useState({
    global: { value: '', matchMode: FilterMatchMode.CONTAINS },
    uid: { value: '', matchMode: FilterMatchMode.CONTAINS },
    name: { value: '', matchMode: FilterMatchMode.CONTAINS },
    explicitMarker: { value: '', matchMode: FilterMatchMode.CONTAINS },
    kafkaTopic: { value: '', matchMode: FilterMatchMode.CONTAINS },
    agentRef: { value: '', matchMode: FilterMatchMode.CONTAINS },
    description: { value: '', matchMode: FilterMatchMode.CONTAINS },
    initialPromptId: { value: '', matchMode: FilterMatchMode.CONTAINS },
    initialPrompt: { value: '', matchMode: FilterMatchMode.CONTAINS },
    'initialPrompt.prompt': { value: '', matchMode: FilterMatchMode.CONTAINS },
    'initialPrompt.dynamicPromptPlotProfiles': {
      value: '',
      matchMode: FilterMatchMode.CONTAINS,
    },
    limitation: { value: '', matchMode: FilterMatchMode.CONTAINS },
    assistantUuid: { value: '', matchMode: FilterMatchMode.CONTAINS },
  });

  const onRowClick = useCallback(({ data }: DataTableRowClickEvent) => {
    // Handle row click
    // eslint-disable-next-line no-console
    console.log(data);
  }, []);

  const explicitMarkerBody = useCallback((rowData: Agent) => {
    return rowData.explicitMarker || '-';
  }, []);

  const limitationBody = useCallback((rowData: Agent) => {
    return rowData.limitation || '-';
  }, []);

  const agentRefBody = useCallback((rowData: Agent) => {
    return rowData.agentRef || '-';
  }, []);

  const assistantUuidBody = useCallback((rowData: Agent) => {
    return rowData.assistantUuid || '-';
  }, []);

  const initialPromptBody = useCallback((rowData: Agent) => {
    const prompt = rowData.initialPrompt?.prompt;

    return <div title={prompt}>{prompt || '-'}</div>;
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <StyledGrid flex={1}>
      <DataTableContainer elevation={3}>
        <DataTable
          id="AgentsTable"
          showGridlines
          resizableColumns
          value={agents}
          selectionMode="single"
          scrollable
          scrollHeight="flex"
          dataKey="uid"
          emptyMessage={t('empty_message')}
          stripedRows
          filters={filters}
          onRowClick={onRowClick}
          filterDisplay="row"
          globalFilterFields={[
            'uid',
            'name',
            'explicitMarker',
            'kafkaTopic',
            'agentRef',
            'limitation',
            'description',
            'initialPromptId',
            'initialPrompt.prompt',
            'initialPrompt.dynamicPromptPlotProfiles',
          ]}
          paginator
          rows={10}
        >
          <Column field="uid" header={t('uid')} sortable filter />
          <Column field="name" header={t('name')} sortable filter />
          <Column
            field="explicitMarker"
            header={t('explicit_marker')}
            sortable
            filter
            body={explicitMarkerBody}
          />
          <Column
            field="kafkaTopic"
            header={t('kafka_topic')}
            sortable
            filter
          />
          <Column
            field="limitation"
            header={t('limitation')}
            sortable
            filter
            body={limitationBody}
          />
          <Column
            field="initialPromptId"
            header={t('initial_prompt_id')}
            sortable
            filter
          />
          <Column
            field="initialPrompt.prompt"
            header={t('initial_prompt')}
            filter
            sortable
            body={initialPromptBody}
            style={{ maxWidth: '300px' }}
          />
          <Column
            field="initialPrompt.dynamicPromptPlotProfiles"
            header={t('dynamic_prompt_plot_profiles')}
            filter
            sortable
          />
          <Column
            field="agentRef"
            header={t('agent_ref')}
            sortable
            filter
            body={agentRefBody}
          />
          <Column
            field="assistantUuid"
            header={t('assistant_uuid')}
            filter
            sortable
            body={assistantUuidBody}
          />
          <Column
            field="description"
            header={t('description')}
            sortable
            filter
          />
        </DataTable>
      </DataTableContainer>
    </StyledGrid>
  );
});

export default AgentsTable;
